/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { AnalyticsEvent } from "@packages/analytics";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link } from "@packages/framework";
import { usePathMatch, useRequestMe, useSession } from "@packages/sdk";
import type {
  HallowElement,
  HallowElementProps,
  TabBarIcon,
  WithAsChild,
} from "@packages/ui/emotion";
import {
  Avatar,
  BibleIcon,
  Button,
  cssMediaQueryMinWidthTablet,
  DashboardIcon,
  determineElementFromAsChild,
  HomeIcon,
  SearchIcon,
  useIsDesktopViewport,
  useToastPositioning,
} from "@packages/ui/emotion";
import type { ThemeType } from "@packages/ui/global/emotion";
import {
  colors,
  numericPixels,
  radius,
  spacing,
  zIndices,
} from "@packages/ui/global/emotion";
import { HallowDataTestId } from "@tools/playwright/global";
import { forwardRef, useEffect, useMemo } from "react";

import { WEB_APP_FULL_SCREEN_ROUTES } from "../../lib/constants/routes.constants";

const hallowElement: HallowElement = "nav";

const TABBAR_HIDDEN_ROUTES = [
  "/admin/:communityId/settings/group-info",
  "/admin/:communityId/events/:id",
  ...WEB_APP_FULL_SCREEN_ROUTES,
];

const buttonStyle: Interpolation = {
  ":hover": { backgroundColor: colors.transparent },
  borderRadius: radius.none,
  height: spacing.full,
  padding: spacing.none,
};
const determinedElementStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  bottom: spacing.none,
  display: "flex",
  [cssMediaQueryMinWidthTablet]: { display: "none" },
  gap: spacing.t,
  justifyContent: "center",
  left: spacing.none,
  position: "sticky",
  width: spacing.full,
  zIndex: zIndices.maskOver,
});
const liStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flex: 1,
  height: spacing.full,
  justifyContent: "center",
};
const ulStyle: Interpolation = {
  display: "flex",
  height: spacing.xxl,
  maxWidth: numericPixels[480],
  width: spacing.full,
};
const hiddenStyle: Interpolation = {
  display: "none",
};

export type TabBarProps = WithAsChild<
  Omit<HallowElementProps<typeof hallowElement>, "children">
>;

export const TabBar = forwardRef<any, TabBarProps>(
  ({ asChild = false, ...props }, ref) => {
    const matcher = usePathMatch();
    const isMissionSupport = matcher("/support-the-mission");
    const isWebCheckout = matcher("/web/subscribe");
    const isTabBarHidden = TABBAR_HIDDEN_ROUTES.some((route) => matcher(route));

    const analytics = useAnalytics();
    const { query: me } = useRequestMe();
    const { status } = useSession();
    const isDesktop = useIsDesktopViewport();
    const { setHasTabBar } = useToastPositioning();

    const links = useMemo<
      {
        analyticsEvent: AnalyticsEvent;
        href: string;
        icon: TabBarIcon;
      }[]
    >(
      () => [
        {
          analyticsEvent: {
            event: HallowAnalyticsEvent.TappedWebAppTabBar,
            properties: {
              tab: "Home",
            },
          },
          href: status === "authenticated" ? "/home" : "/",
          icon: HomeIcon,
        },
        {
          analyticsEvent: {
            event: HallowAnalyticsEvent.TappedDiscover,
          },
          href: "/discover",
          icon: SearchIcon,
        },
        {
          analyticsEvent: {
            event: HallowAnalyticsEvent.TappedWebAppTabBar,
            properties: {
              tab: "Bible",
            },
          },
          href: "/bible",
          icon: BibleIcon,
        },
      ],
      [status],
    );

    useEffect(() => {
      setHasTabBar(!isDesktop && !isWebCheckout && status === "authenticated");
    }, [isDesktop, status]);

    if (isMissionSupport || isWebCheckout || status !== "authenticated")
      return null;

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[determinedElementStyle, isTabBarHidden && hiddenStyle]}
        data-testid={HallowDataTestId.TabBar}
        {...props}
      >
        <ul css={ulStyle}>
          {links.map((link) => {
            const Icon = matcher(
              link.href === "/home" ? "/" : link.href,
              link.href === "/" || link.href === "/home",
            )
              ? link.icon.Filled
              : link.icon.Outline;
            return (
              <li key={link.href} css={liStyle}>
                <Button
                  asChild
                  isFullWidth
                  variant="transparentPrimary"
                  css={buttonStyle}
                >
                  <Link href={link.href}>
                    <Icon />
                  </Link>
                </Button>
              </li>
            );
          })}
          <li css={liStyle}>
            <Button
              asChild
              isFullWidth
              onMouseDown={() => {
                analytics.track({
                  event: HallowAnalyticsEvent.TappedWebAppTabBar,
                  properties: {
                    tab: "Library",
                  },
                });
              }}
              variant="transparentPrimary"
              css={buttonStyle}
            >
              <Link href="/library">
                <Avatar
                  selected={matcher("/library")}
                  imgSrc={me.data?.image_url}
                />
              </Link>
            </Button>
          </li>
          {me.data?.has_access_to_admin_dashboard && (
            <li css={liStyle}>
              <Button
                asChild
                isFullWidth
                onMouseDown={() => {
                  analytics.track({
                    event: HallowAnalyticsEvent.TappedWebAppTabBar,
                    properties: {
                      tab: "Admin Dashboard",
                    },
                  });
                }}
                variant="transparentPrimary"
                css={buttonStyle}
              >
                <Link href="/admin">
                  {matcher("/admin") ? (
                    <DashboardIcon.Filled />
                  ) : (
                    <DashboardIcon.Outline />
                  )}
                </Link>
              </Button>
            </li>
          )}
        </ul>
      </DeterminedElement>
    );
  },
);

TabBar.displayName = "TabBar";
